import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { AuthDialog } from '@/components/AuthDialog';
import { AppProvider } from '@/context/AppContext';

import Home from '@/pages/Home';
import Flights from '@/pages/Flights';
import FlightResults from '@/pages/FlightResults';
import FlightWeekends from '@/pages/FlightWeekends';
import Privacy from '@/pages/Privacy';
import Cookies from '@/pages/Cookies';
import NotFound from '@/pages/NotFound';
import Hotels from '@/pages/Hotels';
import HotelDetail from '@/pages/HotelDetail';
import Experiences from '@/pages/Experiences';
import ExperienceDetail from '@/pages/ExperienceDetail';
import { ConsentBanner } from '@/components/ConsentBanner';
import Deals from '@/pages/Deals';
import Destination from '@/pages/Destination';
import AITripPlanner from '@/pages/AITripPlanner';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import Account from '@/pages/Account';
import Admin from '@/pages/Admin';
import AuthCallback from '@/pages/AuthCallback';
import SeoLanding from '@/pages/SeoLanding';
import Itineraries from '@/pages/Itineraries';
import ItineraryDetail from '@/pages/ItineraryDetail';
import EditorialItinerary from '@/pages/EditorialItinerary';
import AdminItineraries from '@/pages/AdminItineraries';
import CreatorProfile from '@/pages/CreatorProfile';
import CreatorsMarketplace from '@/pages/CreatorsMarketplace';
import CreatorJoin from '@/pages/CreatorJoin';
import CreatorDashboard from '@/pages/CreatorDashboard';
import CreatorBuilder from '@/pages/CreatorBuilder';
import AdminCreators from '@/pages/AdminCreators';
import AdminContent from '@/pages/AdminContent';
import FareIntelligence from '@/pages/FareIntelligence';
import FareAnalytics from '@/pages/FareAnalytics';
import CountryResults from '@/pages/CountryResults';
import AdminCountryFares from '@/pages/AdminCountryFares';
import Visa from '@/pages/Visa';
import Help from '@/pages/Help';
import Store from '@/pages/Store';
import StoreProduct from '@/pages/StoreProduct';
import Tours from '@/pages/Tours';
import TourRoute from '@/pages/TourRoute';
import Esim from '@/pages/Esim';
import Transfers from '@/pages/Transfers';
import CarRentals from '@/pages/CarRentals';
import AdminProviders from '@/pages/AdminProviders';
import AdminEmployees from '@/pages/AdminEmployees';
import AdminAffiliatePerformance from '@/pages/AdminAffiliatePerformance';
import AdminProductionReadiness from '@/pages/AdminProductionReadiness';
import AdminStore from '@/pages/AdminStore';
import AdminTours from '@/pages/AdminTours';
import AdminEsim from '@/pages/AdminEsim';
import AdminCatalog from '@/pages/AdminCatalog';
import AdminSiteConfig from '@/pages/AdminSiteConfig';
import AdminSecurity from '@/pages/AdminSecurity';
import AdminFlights from '@/pages/AdminFlights';
import Insurance from '@/pages/Insurance';
import { AdminUsers, AdminSearches, AdminConversions, AdminEarnings, AdminSystemErrors } from '@/pages/AdminPages';

function AppRouter() {
  const location = useLocation();
  // Reset scroll to top on path change (SPA navigation keeps the previous scroll offset,
  // which on mobile made new pages like an eSIM destination open scrolled to the bottom).
  // Keyed on pathname only, so query-param updates (e.g. flight date picks) don't jump.
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  // Detect Google OAuth callback synchronously (session_id in URL fragment).
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/flights" element={<Flights />} />
      <Route path="/flights/results" element={<FlightResults />} />
      <Route path="/flights/weekends" element={<FlightWeekends />} />
      <Route path="/flights/country-results" element={<CountryResults />} />
      <Route path="/flights/kolkata-to-tokyo" element={<SeoLanding path="flights/kolkata-to-tokyo" />} />
      <Route path="/cheap-flights-from-kolkata" element={<SeoLanding path="cheap-flights-from-kolkata" />} />
      <Route path="/cheap-flights-to-japan" element={<SeoLanding path="cheap-flights-to-japan" />} />
      <Route path="/hotels" element={<Hotels />} />
      <Route path="/hotels/:hotelId" element={<HotelDetail />} />
      <Route path="/experiences" element={<Experiences />} />
      <Route path="/experiences/:code" element={<ExperienceDetail />} />
      <Route path="/deals" element={<Deals />} />
      <Route path="/visa" element={<Visa />} />
      <Route path="/help" element={<Help />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/contact" element={<Help />} />
      <Route path="/store" element={<Store />} />
      <Route path="/store/:slug" element={<StoreProduct />} />
      <Route path="/tours" element={<Tours />} />
      <Route path="/tours/:slug" element={<TourRoute />} />
      <Route path="/tours/:slug/:sub" element={<TourRoute />} />
      <Route path="/esim" element={<Esim />} />
      <Route path="/esim/:country" element={<Esim />} />
      <Route path="/esim/:country/:plan" element={<Esim />} />
      <Route path="/insurance" element={<Insurance />} />
      <Route path="/transfers" element={<Transfers />} />
      <Route path="/car-rentals" element={<CarRentals />} />
      <Route path="/destinations/:slug" element={<Destination />} />
      <Route path="/ai-travel-planner" element={<AITripPlanner />} />
      <Route path="/itineraries" element={<Itineraries />} />
      <Route path="/itineraries/:slug" element={<EditorialItinerary />} />
      <Route path="/creators" element={<CreatorsMarketplace />} />
      <Route path="/creators/itineraries" element={<Itineraries />} />
      <Route path="/creators/join" element={<CreatorJoin />} />
      <Route path="/creator/dashboard" element={<CreatorDashboard />} />
      <Route path="/creator/itineraries/:id" element={<CreatorBuilder />} />
      <Route path="/creators/:username/itineraries/:slug" element={<ItineraryDetail />} />
      <Route path="/creators/:username" element={<CreatorProfile />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/account" element={<Account />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/creators" element={<AdminCreators />} />
      <Route path="/admin/campaigns" element={<AdminCreators />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/searches" element={<AdminSearches />} />
      <Route path="/admin/conversions" element={<AdminConversions />} />
      <Route path="/admin/earnings" element={<AdminEarnings />} />
      <Route path="/admin/system/errors" element={<AdminSystemErrors />} />
      <Route path="/admin/content" element={<AdminContent />} />
      <Route path="/admin/itineraries" element={<AdminItineraries />} />
      <Route path="/admin/catalog" element={<AdminCatalog />} />
      <Route path="/admin/site-config" element={<AdminSiteConfig />} />
      <Route path="/admin/security" element={<AdminSecurity />} />
      <Route path="/admin/flights" element={<AdminFlights />} />
      <Route path="/admin/costs" element={<AdminSecurity />} />
      <Route path="/admin/store" element={<Navigate to="/admin/catalog?entity=store_product" replace />} />
      <Route path="/admin/tours" element={<Navigate to="/admin/catalog?entity=tour" replace />} />
      <Route path="/admin/esim" element={<Navigate to="/admin/catalog?entity=esim_plan" replace />} />
      <Route path="/admin/store-legacy" element={<AdminStore />} />
      <Route path="/admin/tours-legacy" element={<AdminTours />} />
      <Route path="/admin/esim-legacy" element={<AdminEsim />} />
      <Route path="/admin/providers" element={<AdminProviders />} />
      <Route path="/admin/employees" element={<AdminEmployees />} />
      <Route path="/admin/affiliate-performance" element={<AdminAffiliatePerformance />} />
      <Route path="/admin/production-readiness" element={<AdminProductionReadiness />} />
      <Route path="/admin/fare-intelligence" element={<FareIntelligence />} />
      <Route path="/admin/fare-intelligence/analytics" element={<FareAnalytics />} />
      <Route path="/admin/country-fares" element={<AdminCountryFares />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <AuthDialog />
          <Toaster position="top-center" richColors />
          <ConsentBanner />
        </AuthProvider>
      </AppProvider>
    </div>
  );
}

export default App;
